import { useEffect, useRef } from "react";

/**
 * Photo-realistic night sky on a multi-pass canvas.
 *
 * Layers (drawn in order, additive where appropriate):
 *  1. Sky base gradient — zenith deep navy → horizon slightly warmer
 *     (atmospheric Rayleigh + light pollution).
 *  2. Airglow band — very faint greenish ring low in the sky.
 *  3. Milky Way nebulosity — many overlapping soft warm/cool dust patches,
 *     pre-blurred, plus dark dust-lane subtraction to give structure.
 *  4. Faint emission/reflection nebulae — a couple of subtle pink (H-alpha)
 *     and blue patches.
 *  5. Star field — thousands of stars with Moffat/Gaussian PSFs, proper
 *     spectral-class colors, magnitude power-law distribution, and
 *     atmospheric extinction (dimmer + redder near the horizon).
 *  6. Twinkle pass — only a small fraction of stars twinkle, redrawn each
 *     animation frame on top of a cached composite.
 */

const seeded = (seed) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};

// Realistic stellar colors (loose mapping to spectral classes)
const STAR_COLORS = [
  { r: 175, g: 200, b: 255, w: 3 },   // O — hot blue (very rare)
  { r: 215, g: 230, b: 255, w: 9 },   // B — blue-white
  { r: 240, g: 245, b: 255, w: 24 },  // A — white
  { r: 255, g: 250, b: 240, w: 28 },  // F — yellow-white
  { r: 255, g: 240, b: 210, w: 18 },  // G — yellow
  { r: 255, g: 210, b: 160, w: 11 },  // K — orange
  { r: 255, g: 175, b: 120, w: 5 },   // M — red-orange
  { r: 255, g: 140, b: 100, w: 2 },   // late M (rare)
];

const pickColor = (rand) => {
  const total = STAR_COLORS.reduce((s, c) => s + c.w, 0);
  let r = rand() * total;
  for (const c of STAR_COLORS) {
    r -= c.w;
    if (r <= 0) return c;
  }
  return STAR_COLORS[2];
};

// Tilted Milky Way axis
const MW_ANGLE = (-16 * Math.PI) / 180;

const projOnBand = (x, y, cx, cy) => {
  const dx = x - cx;
  const dy = y - cy;
  return {
    along: dx * Math.cos(MW_ANGLE) + dy * Math.sin(MW_ANGLE),
    across: -dx * Math.sin(MW_ANGLE) + dy * Math.cos(MW_ANGLE),
  };
};

const milkyWayBoost = (x, y, w, h) => {
  const cx = w * 0.5;
  const cy = h * 0.45;
  const { across } = projOnBand(x, y, cx, cy);
  const bandHalf = h * 0.18;
  const t = Math.max(0, 1 - Math.abs(across) / bandHalf);
  return t * t;
};

/* ---------- Layer painters (run once into offscreen canvases) ---------- */

function paintSkyBase(ctx, w, h) {
  // Vertical gradient: deep navy near zenith → faintly warmer toward horizon
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#02030a");
  grad.addColorStop(0.55, "#03050d");
  grad.addColorStop(0.85, "#0a0a18");
  grad.addColorStop(1, "#0f0c1a");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Subtle horizon airglow (faint greenish-orange band, very low alpha)
  const ag = ctx.createLinearGradient(0, h * 0.7, 0, h);
  ag.addColorStop(0, "rgba(80, 90, 80, 0)");
  ag.addColorStop(0.6, "rgba(95, 80, 70, 0.06)");
  ag.addColorStop(1, "rgba(120, 90, 70, 0.12)");
  ctx.fillStyle = ag;
  ctx.fillRect(0, h * 0.7, w, h * 0.3);
}

function paintMilkyWay(ctx, w, h, seed) {
  const rand = seeded(seed);
  const cx = w * 0.5;
  const cy = h * 0.45;

  // Use canvas filter for organic softness if supported.
  ctx.save();
  if (typeof ctx.filter !== "undefined") {
    ctx.filter = "blur(28px)";
  }
  ctx.globalCompositeOperation = "screen";

  // Bright dust patches along the band
  const PATCH_COUNT = 110;
  for (let i = 0; i < PATCH_COUNT; i++) {
    const along = (rand() - 0.5) * w * 1.6;
    const across = (rand() - 0.5) * h * 0.34;
    const x = cx + along * Math.cos(MW_ANGLE) - across * Math.sin(MW_ANGLE);
    const y = cy + along * Math.sin(MW_ANGLE) + across * Math.cos(MW_ANGLE);
    const r = 45 + rand() * 220;

    // Bias intensity by closeness to band center (concentration)
    const bandFalloff = Math.max(0, 1 - Math.abs(across) / (h * 0.22));
    const intensity = bandFalloff * (0.4 + rand() * 0.6);

    const tint = rand();
    let col;
    if (tint < 0.45) col = `rgba(190, 180, 215, ${0.06 * intensity})`;       // cool blue-violet dust
    else if (tint < 0.78) col = `rgba(220, 200, 170, ${0.07 * intensity})`;  // warm dust
    else if (tint < 0.94) col = `rgba(160, 150, 195, ${0.05 * intensity})`;  // muted bluish
    else col = `rgba(190, 110, 110, ${0.045 * intensity})`;                  // faint H-alpha pink

    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, col);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Darker dust lanes — punch holes in the brightness
  ctx.globalCompositeOperation = "destination-out";
  const LANE_COUNT = 35;
  for (let i = 0; i < LANE_COUNT; i++) {
    const along = (rand() - 0.5) * w * 1.4;
    const across = (rand() - 0.5) * h * 0.18;
    const x = cx + along * Math.cos(MW_ANGLE) - across * Math.sin(MW_ANGLE);
    const y = cy + along * Math.sin(MW_ANGLE) + across * Math.cos(MW_ANGLE);
    const r = 40 + rand() * 130;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(0,0,0,${0.35 + rand() * 0.35})`);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function paintNebulae(ctx, w, h, seed) {
  // A couple of subtle named-region nebulae + a faint distant galaxy
  const rand = seeded(seed);
  ctx.save();
  if (typeof ctx.filter !== "undefined") ctx.filter = "blur(22px)";
  ctx.globalCompositeOperation = "screen";

  const nebulae = [
    { x: w * 0.18, y: h * 0.32, r: 110, c: "rgba(200, 120, 130, 0.07)" },
    { x: w * 0.78, y: h * 0.28, r: 90, c: "rgba(140, 165, 215, 0.06)" },
    { x: w * 0.62, y: h * 0.62, r: 130, c: "rgba(120, 170, 175, 0.05)" },
  ];
  nebulae.forEach((n) => {
    const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
    grad.addColorStop(0, n.c);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
    ctx.fill();
  });

  for (let i = 0; i < 25; i++) {
    const x = rand() * w;
    const y = rand() * h * 0.7;
    const r = 30 + rand() * 60;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(180, 165, 200, ${0.025 + rand() * 0.02})`);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// Single pine tree silhouette — stacked irregular triangle layers
function drawPineTree(ctx, cx, baseY, height, color, seed) {
  const rand = seeded(seed);
  const layers = 6 + Math.floor(rand() * 2);
  const trunkW = Math.max(2, height * 0.016);
  const trunkH = height * 0.1;

  ctx.fillStyle = color;
  // Trunk (slightly tapered)
  ctx.beginPath();
  ctx.moveTo(cx - trunkW / 2, baseY + 4);
  ctx.lineTo(cx - trunkW * 0.4, baseY - trunkH);
  ctx.lineTo(cx + trunkW * 0.4, baseY - trunkH);
  ctx.lineTo(cx + trunkW / 2, baseY + 4);
  ctx.closePath();
  ctx.fill();

  // Branch layers (bottom to top)
  const branchTop = baseY - trunkH * 0.6;
  const treeBodyH = height - trunkH * 0.6;
  const baseLayerW = height * 0.34;

  for (let i = 0; i < layers; i++) {
    const t = i / (layers - 1);
    const layerW = baseLayerW * (1 - t * 0.78);
    const yTop = branchTop - treeBodyH * (0.16 + t * 0.84);
    const yBottom = yTop + treeBodyH * (0.28 + rand() * 0.04);

    // Asymmetric apex offset for organic feel
    const apexShift = (rand() - 0.5) * height * 0.012;
    const leftShift = (rand() - 0.5) * 4;
    const rightShift = (rand() - 0.5) * 4;

    ctx.beginPath();
    ctx.moveTo(cx - layerW / 2 + leftShift, yBottom);
    // Mid-left jag
    ctx.lineTo(cx - layerW * 0.28, yBottom - treeBodyH * 0.04);
    ctx.lineTo(cx + apexShift, yTop);
    ctx.lineTo(cx + layerW * 0.28, yBottom - treeBodyH * 0.04);
    ctx.lineTo(cx + layerW / 2 + rightShift, yBottom);
    ctx.closePath();
    ctx.fill();
  }
}

function paintForest(ctx, w, h, seed) {
  const rand = seeded(seed);

  ctx.save();
  ctx.globalCompositeOperation = "source-over";

  // Distant tree line — smaller, slightly hazed
  if (typeof ctx.filter !== "undefined") ctx.filter = "blur(0.6px)";
  for (let i = 0; i < 22; i++) {
    const x = (i / 21) * w + (rand() - 0.5) * w * 0.045;
    const treeH = h * (0.1 + rand() * 0.09);
    drawPineTree(ctx, x, h * 0.99, treeH, "rgba(6,8,18,0.85)", seed + i);
  }
  if (typeof ctx.filter !== "undefined") ctx.filter = "none";

  // Mid-ground trees
  for (let i = 0; i < 12; i++) {
    const x = (i / 11) * w + (rand() - 0.5) * w * 0.07;
    const treeH = h * (0.18 + rand() * 0.13);
    drawPineTree(ctx, x, h, treeH, "rgba(2,3,9,0.95)", seed + 200 + i);
  }

  // Foreground trees — taller, fully black
  for (let i = 0; i < 7; i++) {
    const x = w * (0.08 + (i / 6) * 0.84) + (rand() - 0.5) * w * 0.08;
    const treeH = h * (0.3 + rand() * 0.18);
    drawPineTree(ctx, x, h + 6, treeH, "#000000", seed + 400 + i);
  }
  ctx.restore();
}

// Moon glow — halo only. The actual disc is rendered as a DOM element
// at the navbar's z-index so the light stays behind, the moon is on top.
function paintMoon(ctx, cx, cy, radius, seed) {
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  const haloR = Math.max(radius * 3.4, 16);
  const haloGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, haloR);
  haloGrad.addColorStop(0, "rgba(245, 240, 222, 0.42)");
  haloGrad.addColorStop(0.2, "rgba(238, 230, 208, 0.18)");
  haloGrad.addColorStop(0.5, "rgba(220, 210, 190, 0.05)");
  haloGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = haloGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, haloR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ISO / sensor noise — the single biggest "photographic" tell.
// Draws to an offscreen, then composites over with `screen` so it brightens
// dark pixels slightly without clobbering the rendered scene.
function paintGrain(ctx, w, h, dpr, seed) {
  const rand = seeded(seed);
  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  const off = document.createElement("canvas");
  off.width = pw;
  off.height = ph;
  const octx = off.getContext("2d");
  const img = octx.createImageData(pw, ph);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    // Mostly faint warm-gray noise, occasional brighter pixel (hot pixels)
    const base = Math.pow(rand(), 5) * 26;
    const cool = rand() < 0.25 ? rand() * 4 : 0;
    const warm = rand() < 0.18 ? rand() * 5 : 0;
    data[i] = Math.round(base + warm);
    data[i + 1] = Math.round(base * 0.95);
    data[i + 2] = Math.round(base * 0.9 + cool);
    data[i + 3] = 255;
  }
  octx.putImageData(img, 0, 0);

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "screen";
  ctx.globalAlpha = 0.55;
  ctx.drawImage(off, 0, 0);
  ctx.restore();
}

// Open star cluster (Pleiades-style) — tight group with faint blue nebulosity
function paintCluster(ctx, cx, cy, count, seed) {
  const rand = seeded(seed);
  ctx.save();
  if (typeof ctx.filter !== "undefined") ctx.filter = "blur(14px)";
  ctx.globalCompositeOperation = "screen";
  const neb = ctx.createRadialGradient(cx, cy, 0, cx, cy, 60);
  neb.addColorStop(0, "rgba(150, 175, 220, 0.1)");
  neb.addColorStop(0.5, "rgba(150, 175, 220, 0.03)");
  neb.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = neb;
  ctx.beginPath();
  ctx.arc(cx, cy, 60, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < count; i++) {
    const a = rand() * Math.PI * 2;
    const r = Math.pow(rand(), 1.5) * 32;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    const size = 0.55 + rand() * 0.7;
    const halo = size * (1.6 + rand() * 1.2);
    const intensity = 0.65 + rand() * 0.3;
    const star = {
      x, y, size, halo,
      brightness: intensity,
      color: { r: 220, g: 230, b: 255 },
      tier: 1,
    };
    drawStar(ctx, star);
  }
  ctx.restore();
}

/* ---------- Star generation ---------- */

function buildStars(width, height, count, seed) {
  const rand = seeded(seed);
  const stars = [];
  let attempts = 0;
  while (stars.length < count && attempts < count * 5) {
    attempts++;
    const x = rand() * width;
    const y = rand() * height;
    const mw = milkyWayBoost(x, y, width, height);
    if (rand() > 0.6 + mw * 0.4) continue;

    const u = rand();
    const magNorm = Math.pow(u, 5.5);

    let tier = 0;
    if (magNorm > 0.92) tier = 1;   // small halo only — no big bright stars
    // else tier 0 (faint pinprick)

    const sizeBase =
      tier === 0 ? 0.4 + magNorm * 0.55 :
                   0.6 + magNorm * 0.5;

    const haloRadius =
      tier === 0 ? 0 :
                   sizeBase * (1.4 + magNorm * 1);

    let intensity =
      tier === 0 ? 0.35 + magNorm * 0.4 :
                   0.55 + magNorm * 0.3;

    // Atmospheric extinction near the horizon: dim and redden
    const altitude = 1 - y / height; // 1 zenith, 0 horizon
    const ext = altitude < 0.2 ? altitude / 0.2 : 1;
    intensity *= 0.55 + 0.45 * ext;

    const baseColor = pickColor(rand);
    const reddening = 1 - ext;
    const color = {
      r: Math.round(baseColor.r),
      g: Math.round(baseColor.g - reddening * 35),
      b: Math.round(baseColor.b - reddening * 70),
    };

    // Most stars get a subtle low-amplitude twinkle. A smaller subset gets
    // a perceptibly stronger flicker — the way real atmospheric scintillation
    // affects individual sight lines.
    const strongTwinkle = tier >= 1 && rand() < 0.18;
    const subtleTwinkle = !strongTwinkle && rand() < 0.55;
    const twinkle = strongTwinkle || subtleTwinkle;
    const tAmp = strongTwinkle ? 0.16 + rand() * 0.16 : 0.04 + rand() * 0.06;

    stars.push({
      x,
      y,
      size: sizeBase,
      halo: haloRadius,
      brightness: intensity,
      color,
      tier,
      twinkle,
      tPhase: rand() * Math.PI * 2,
      tSpeed: 0.25 + rand() * 1.0,
      tAmp,
    });
  }
  stars.sort((a, b) => a.brightness - b.brightness);
  return stars;
}

function drawStar(ctx, s, intensityOverride) {
  const intensity = intensityOverride ?? s.brightness;
  const { r, g, b } = s.color;

  // Halo (Gaussian-like via radial gradient with smooth falloff)
  if (s.halo > 0) {
    const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.halo);
    grad.addColorStop(0, `rgba(${r},${g},${b},${0.45 * intensity})`);
    grad.addColorStop(0.15, `rgba(${r},${g},${b},${0.22 * intensity})`);
    grad.addColorStop(0.4, `rgba(${r},${g},${b},${0.07 * intensity})`);
    grad.addColorStop(0.75, `rgba(${r},${g},${b},${0.015 * intensity})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.halo, 0, Math.PI * 2);
    ctx.fill();
  }

  // Core — desaturate toward white as brightness rises (saturation in the eye)
  const whiten = s.tier === 1 ? 0.2 : 0.1;
  const cr = Math.round(r + (255 - r) * whiten);
  const cg = Math.round(g + (255 - g) * whiten);
  const cb = Math.round(b + (255 - b) * whiten);
  ctx.fillStyle = `rgba(${cr},${cg},${cb},${Math.min(1, intensity * 1.1)})`;
  ctx.beginPath();
  ctx.arc(s.x, s.y, Math.max(0.3, s.size * 0.5), 0, Math.PI * 2);
  ctx.fill();
}

function drawSubtleSpike(ctx, s, intensity) {
  // Very thin tapered diffraction spike on tier-3 stars only.
  const len = 22 + s.size * 12;
  const alpha = Math.min(0.35, intensity * 0.35);
  // Horizontal
  let g1 = ctx.createLinearGradient(s.x - len, s.y, s.x + len, s.y);
  g1.addColorStop(0, "rgba(255,255,255,0)");
  g1.addColorStop(0.5, `rgba(255,255,255,${alpha})`);
  g1.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g1;
  ctx.fillRect(s.x - len, s.y - 0.4, len * 2, 0.8);
  // Vertical
  let g2 = ctx.createLinearGradient(s.x, s.y - len, s.x, s.y + len);
  g2.addColorStop(0, "rgba(255,255,255,0)");
  g2.addColorStop(0.5, `rgba(255,255,255,${alpha})`);
  g2.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g2;
  ctx.fillRect(s.x - 0.4, s.y - len, 0.8, len * 2);
}

/* ---------- Component ---------- */

export default function StarField({ density = 1, moonX = 0.5, moonY = 56, moonRadius }) {
  const canvasRef = useRef(null);
  const baseRef = useRef(null); // offscreen for the static composite
  const rafRef = useRef(null);
  const starsRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0 });
  const meteorRef = useRef(null); // current active meteor or null
  const nextMeteorRef = useRef(0); // timestamp (s) when next meteor should spawn
  const flickersRef = useRef([]); // active flicker events
  const nextFlickerRef = useRef(0); // timestamp (s) when next flicker should fire

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true });

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Build stars
      const area = w * h;
      const target = Math.round((area / (1920 * 1080)) * 8000 * density);
      const stars = buildStars(w, h, target, 1731);
      starsRef.current = stars;

      // Pre-render the static composite (sky, MW, nebulae, non-twinkling stars)
      const off = document.createElement("canvas");
      off.width = canvas.width;
      off.height = canvas.height;
      const octx = off.getContext("2d", { alpha: true });
      octx.setTransform(dpr, 0, 0, dpr, 0, 0);

      paintSkyBase(octx, w, h);
      paintMilkyWay(octx, w, h, 91);
      paintNebulae(octx, w, h, 17);

      // Open clusters
      paintCluster(octx, w * 0.72, h * 0.42, 9, 211);
      paintCluster(octx, w * 0.22, h * 0.66, 7, 313);

      // Stars (additive)
      octx.globalCompositeOperation = "lighter";
      for (let i = 0; i < stars.length; i++) {
        if (!stars[i].twinkle) drawStar(octx, stars[i]);
      }
      octx.globalCompositeOperation = "source-over";

      // Moon glow — position from props
      const moonR = moonRadius ?? Math.max(20, Math.min(w, h) * 0.028);
      paintMoon(octx, w * moonX, moonY, moonR, 555);

      // Sensor noise grain — paint LAST so it sits on top
      paintGrain(octx, w, h, dpr, 4242);

      baseRef.current = off;
      // Seed first meteor 2-5s after load
      nextMeteorRef.current = (performance.now() / 1000) + 2 + Math.random() * 3;
    };

    setup();

    const render = (now) => {
      const t = now / 1000;
      const { w, h, dpr } = sizeRef.current;
      // Blit pre-rendered base (sky + non-twinkling stars)
      // We have to clear and draw scaled correctly. The offscreen is in device-px,
      // so use drawImage with destination logical size.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(baseRef.current, 0, 0);
      ctx.restore();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Animate twinkle layer on top
      ctx.globalCompositeOperation = "lighter";
      const stars = starsRef.current;

      // Maintain occasional flicker events: pick a random twinkle star and
      // give it a brief sharp brightness pop. Multiple can run simultaneously.
      if (t >= nextFlickerRef.current && stars.length) {
        const candidates = [];
        for (let i = 0; i < stars.length; i++) {
          if (stars[i].twinkle) candidates.push(i);
        }
        if (candidates.length) {
          const idx = candidates[Math.floor(Math.random() * candidates.length)];
          flickersRef.current.push({
            idx,
            start: t,
            duration: 0.18 + Math.random() * 0.32, // 180-500ms
            amp: 0.6 + Math.random() * 0.8,        // 0.6-1.4 brightness boost
          });
        }
        // Next flicker in 0.4-2.5s — feels organic
        nextFlickerRef.current = t + 0.4 + Math.random() * 2.1;
      }

      // Build quick lookup of active flicker boosts keyed by star index
      const activeFlickers = flickersRef.current;
      const flickerByIdx = new Map();
      for (let i = activeFlickers.length - 1; i >= 0; i--) {
        const f = activeFlickers[i];
        const age = t - f.start;
        if (age >= f.duration) {
          activeFlickers.splice(i, 1);
          continue;
        }
        // Asymmetric envelope: fast attack, slower decay
        const x = age / f.duration;
        const env = x < 0.25 ? x / 0.25 : 1 - (x - 0.25) / 0.75;
        flickerByIdx.set(f.idx, (flickerByIdx.get(f.idx) || 0) + f.amp * env);
      }

      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (!s.twinkle) continue;
        const flick =
          1 - s.tAmp + s.tAmp * (0.5 + 0.5 * Math.sin(t * s.tSpeed + s.tPhase));
        const boost = flickerByIdx.get(i) || 0;
        drawStar(ctx, s, s.brightness * flick * (1 + boost));
      }

      // Shooting star — tiny, thin, occasional. Travels left -> right with a
      // slight downward angle so it feels like atmospheric entry. Super fast
      // streak with a fade-out tail.
      if (!meteorRef.current && t >= nextMeteorRef.current) {
        const startY = h * (0.08 + Math.random() * 0.32);
        const angle = (4 + Math.random() * 5) * (Math.PI / 180); // 4-9 deg downward
        const speed = 2400 + Math.random() * 1200; // px/s — SUPER fast
        meteorRef.current = {
          x: -40,
          y: startY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          // Burn out before exiting screen so the user sees a fade
          burnDuration: 0.32 + Math.random() * 0.18, // 320-500ms streak
          last: t,
          trail: [],
        };
      }

      const m = meteorRef.current;
      if (m) {
        const dt = Math.min(0.05, t - m.last);
        m.last = t;
        m.life += dt;
        // Stop adding new trail points after burnout — only the tail fades
        if (m.life < m.burnDuration) {
          m.x += m.vx * dt;
          m.y += m.vy * dt;
          m.trail.push({ x: m.x, y: m.y, age: 0 });
        }
        for (let i = 0; i < m.trail.length; i++) m.trail[i].age += dt;
        // Longer trail — high speed produces a long visible streak
        while (m.trail.length > 60) m.trail.shift();

        const TRAIL_LIFE = 0.6;
        // Head fade — bright during burn, then quick fade
        const headFade =
          m.life < m.burnDuration
            ? 1
            : Math.max(0, 1 - (m.life - m.burnDuration) / 0.18);

        ctx.globalCompositeOperation = "lighter";
        for (let i = 1; i < m.trail.length; i++) {
          const p0 = m.trail[i - 1];
          const p1 = m.trail[i];
          const a = Math.max(0, 1 - p1.age / TRAIL_LIFE);
          if (a <= 0) continue;
          ctx.strokeStyle = `rgba(245, 235, 220, ${0.65 * a})`;
          ctx.lineWidth = 0.7 * a + 0.2;
          ctx.beginPath();
          ctx.moveTo(p0.x, p0.y);
          ctx.lineTo(p1.x, p1.y);
          ctx.stroke();
        }

        // Bright tiny head with faint halo (only during active burn)
        if (headFade > 0.05 && m.life < m.burnDuration) {
          const haloR = 3.6;
          const grad = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, haloR);
          grad.addColorStop(0, `rgba(255, 245, 220, ${0.85 * headFade})`);
          grad.addColorStop(0.5, `rgba(245, 230, 200, ${0.22 * headFade})`);
          grad.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(m.x, m.y, haloR, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = `rgba(255, 250, 235, ${0.95 * headFade})`;
          ctx.beginPath();
          ctx.arc(m.x, m.y, 0.8, 0, Math.PI * 2);
          ctx.fill();
        }

        // Retire when trail has fully aged out
        if (m.life > m.burnDuration + TRAIL_LIFE + 0.1) {
          meteorRef.current = null;
          nextMeteorRef.current = t + 6 + Math.random() * 8;
        }
      }

      ctx.globalCompositeOperation = "source-over";
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setup, 200);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimer);
    };
  }, [density, moonX, moonY, moonRadius]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-[3]"
      data-testid="hero-starfield"
    />
  );
}
